import React, { useState } from 'react';
import { 
  Zap, 
  Users, 
  ShoppingBag, 
  Mic, 
  Fuel, 
  Store, 
  ShieldCheck, 
  BusFront,
  X,
  Sparkles,
  ShieldAlert,
  Search,
  Droplet
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface FloatingFeatureMenuProps {
  onFeatureSelect: (featureId: string) => void;
  isAjoEnabled?: boolean;
}

const features = [
  {
    id: 'power',
    name: 'Light Watch',
    icon: Zap,
    color: 'bg-yellow-500',
    textColor: 'text-yellow-600'
  },
  {
    id: 'ajo',
    name: 'Digital Ajo',
    icon: Users,
    color: 'bg-purple-500',
    textColor: 'text-purple-600'
  },
  {
    id: 'market',
    name: 'Market Basket',
    icon: ShoppingBag,
    color: 'bg-orange-500',
    textColor: 'text-orange-600'
  },
  {
    id: 'voice',
    name: 'Voice Report',
    icon: Mic,
    color: 'bg-blue-500',
    textColor: 'text-blue-600'
  },
  {
    id: 'fuel',
    name: 'Fuel Watch',
    icon: Fuel,
    color: 'bg-red-500',
    textColor: 'text-red-600'
  },
  {
    id: 'marketplace',
    name: 'Swap & Shop',
    icon: Store,
    color: 'bg-indigo-500',
    textColor: 'text-indigo-600'
  },
  {
    id: 'safepay',
    name: 'SafePay',
    icon: ShieldCheck,
    color: 'bg-emerald-500',
    textColor: 'text-emerald-600'
  },
  {
    id: 'bus',
    name: 'Bus Watch',
    icon: BusFront,
    color: 'bg-cyan-500',
    textColor: 'text-cyan-600'
  },
  {
    id: 'crime',
    name: 'Crime Watch',
    icon: ShieldAlert,
    color: 'bg-red-600',
    textColor: 'text-red-700'
  },
  {
    id: 'lost-found',
    name: 'Lost & Found',
    icon: Search,
    color: 'bg-blue-600',
    textColor: 'text-blue-700'
  },
  {
    id: 'water',
    name: 'Water Vendors',
    icon: Droplet,
    color: 'bg-cyan-600',
    textColor: 'text-cyan-700'
  }
];

export function FloatingFeatureMenu({ onFeatureSelect, isAjoEnabled = true }: FloatingFeatureMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleFeatureClick = (featureId: string) => {
    onFeatureSelect(featureId);
    setIsOpen(false);
  };

  // Filter out Digital Ajo if not enabled
  const visibleFeatures = features.filter(feature => {
    if (feature.id === 'ajo') {
      return isAjoEnabled;
    }
    return true;
  });

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm"
          />
        )}
      </AnimatePresence>

      {/* Feature Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-48 left-4 z-50 bg-white rounded-3xl shadow-2xl border-2 border-zinc-200 p-4 w-72"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-zinc-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-sm font-black text-zinc-900 uppercase tracking-tight">
                  All Features
                </h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-zinc-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-zinc-500" />
              </button>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-3">
              {visibleFeatures.map((feature, index) => (
                <motion.button
                  key={feature.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => handleFeatureClick(feature.id)}
                  className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-zinc-50 hover:bg-zinc-100 active:scale-95 transition-all border border-zinc-200"
                >
                  <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center shadow-md`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-[10px] font-black text-zinc-900 uppercase tracking-tight text-center leading-none">
                    {feature.name}
                  </span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`
          fixed bottom-32 left-4 z-50
          w-14 h-14 rounded-full 
          ${isOpen ? 'bg-red-500' : 'bg-gradient-to-br from-green-500 to-emerald-600'}
          text-white
          shadow-2xl shadow-green-500/40
          flex items-center justify-center
          transition-colors duration-200
        `}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X className="w-6 h-6" />
            </motion.div>
          ) : (
            <motion.div
              key="menu"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Sparkles className="w-6 h-6" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </>
  );
}